import React, {useState, useMemo, useLayoutEffect, useEffect} from "react";
import {Row, Col, Checkbox, Radio, Tooltip, message, Modal, Input, Button, Card} from "antd";
import moment from "moment";
import {useObserver} from "mobx-react-lite";
import {DownloadOutlined, SaveOutlined, EditOutlined, DeleteOutlined} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/tr_TR";
import {DatePicker} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "../../hooks/useStore";

import {SelectField, DateRangePicker} from "./reportPageStyles";
import {CityFilter, CompanyFilter, SubCompanyFilter} from "../_defaultComponents/FilterComponents";
import {ZoneSelect} from "../_defaultComponents/ZoneSelect/ZoneSelect";
import {ZoneManagementSelect} from "../_defaultComponents/ZoneManagementSelect/ZoneManagementSelect";
import {arrayAlphabeticalSort, b64DecodeToUrl, normalizeText} from "../../utils/helpers";
import {activationSubSteps} from "../../constants/constants";
import {DownloadButton} from "../_defaultComponents/DownloadButton/DownloadButton";
import {useFetch} from "../../hooks/useFetch";

const SHIFTS_V2_REPORT = "shiftsV2";
const COMPANY_ORDERS_REPORT = "companyOrders";

export const ReportsPage = () => {
    //#region variables
    const stores = useStore();
    const {t, i18n} = useTranslation(["reportPage", "activationSubSteps"]);
    const [reportType, setReportType] = useState("");
    const [prevReportType, setPrevReportType] = useState("");
    const [dateRange, setDateRange] = useState([]);
    const [orderList, setOrderList] = useState(null);
    const [hoursList, setHoursList] = useState(null);
    const [shiftsList, setShiftsList] = useState(null);
    const [poolShiftList, setPoolShiftList] = useState(null);
    const [shiftEditLogsList, setShiftEditLogsList] = useState(null);
    const [courierFeedbackList, setCourierFeedbackList] = useState(null);
    const [subCompaniesList, setSubCompaniesList] = useState(null);
    const [userReportList, setUserReportList] = useState(null);
    const [courierDedicationList, setCourierDedicationList] = useState(null);
    const [subcompanySalesList, setSubcompanySalesList] = useState(null);
    const [subcompanyContractsList, setSubcompanyContractsList] = useState(null);
    const [additionalColumns, setAdditionalColumns] = useState([]);
    const [dateOption, setDateOption] = useState(undefined);
    const [reportColumns, setReportColumns] = useState(undefined);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(undefined);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedSubCompanies, setSelectedSubCompanies] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedCities, setSelectedCities] = useState([]);
    const [poolGroups, setPoolGroups] = useState([]);
    const [poolIdsShifts, setPoolIdsShifts] = useState([]);
    const [workType, setWorkType] = useState("all");
    const [activeSubCompany, setActiveSubCompany] = useState("all");
    const [contractDate, setContractDate] = useState(null);
    const [downloadReport, setDownloadReport] = useState(false);
    const [courierReportList, setCourierReportList] = useState(null);
    const [courierReportDisabled, setCourierReportDisabled] = useState(undefined);
    const [courierReportActivationStep, setCourierReportActivationStep] = useState(undefined);
    const [courierReportActivationSubStep, setCourierReportActivationSubStep] = useState(undefined);
    const [courierReportZoneManagementId, setCourierReportZoneManagementId] = useState(undefined);
    const [courierReportZoneId, setCourierReportZoneId] = useState(undefined);
    const [courierReportLeaveStatus, setCourierReportLeaveStatus] = useState(undefined);
    const [courierReportApplicationDateRange, setCourierReportApplicationDateRange] = useState([]);
    const [courierReportOfficialQuitDateRange, setCourierReportOfficialQuitDateRange] = useState(
        []
    );
    const [selectedTemplateId, setSelectedTemplateId] = useState(undefined);
    const [saveTemplateModalVisible, setSaveTemplateModalVisible] = useState(false);
    const [editTemplateModalVisible, setEditTemplateModalVisible] = useState(false);
    const [deleteTemplateModalVisible, setDeleteTemplateModalVisible] = useState(false);
    const [templateName, setTemplateName] = useState("");
    //#endregion

    const resetCourierReportToOriginal = () => {
        setCourierReportDisabled(undefined);
        setCourierReportActivationStep(undefined);
        setCourierReportActivationSubStep(undefined);
        setCourierReportZoneManagementId(undefined);
        setCourierReportZoneId(undefined);
        setCourierReportLeaveStatus(undefined);
        setCourierReportApplicationDateRange([]);
        setCourierReportOfficialQuitDateRange([]);
        setSelectedColumns(reportColumns ? reportColumns.map(r => r.key) : []);
        setSelectedTemplateId(undefined);
    };

    const applyCourierReportTemplate = template => {
        if (!template) return;
        const {filters = {}, selectedColumns: templateColumns = []} = template;
        setCourierReportDisabled(filters.disabled);
        setCourierReportActivationStep(filters.activationStep);
        setCourierReportActivationSubStep(filters.activationSubStep);
        setCourierReportZoneManagementId(filters.zoneManagementId);
        setCourierReportZoneId(filters.zoneId);
        setCourierReportLeaveStatus(filters.leaveStatus);
        setCourierReportApplicationDateRange(
            filters.applicationDateStart && filters.applicationDateEnd
                ? [new Date(filters.applicationDateStart), new Date(filters.applicationDateEnd)]
                : []
        );
        setCourierReportOfficialQuitDateRange(
            filters.officialQuitDateStart && filters.officialQuitDateEnd
                ? [new Date(filters.officialQuitDateStart), new Date(filters.officialQuitDateEnd)]
                : []
        );
        if (templateColumns.length > 0) {
            setSelectedColumns(templateColumns);
        }
        setSelectedTemplateId(template._id);
    };

    const getCourierReportFiltersFromState = () => ({
        disabled: courierReportDisabled,
        activationStep: courierReportActivationStep,
        activationSubStep: courierReportActivationSubStep,
        zoneManagementId: courierReportZoneManagementId,
        zoneId: courierReportZoneId,
        leaveStatus: courierReportLeaveStatus,
        applicationDateStart: courierReportApplicationDateRange[0]?.toISOString?.() || null,
        applicationDateEnd: courierReportApplicationDateRange[1]?.toISOString?.() || null,
        officialQuitDateStart: courierReportOfficialQuitDateRange[0]?.toISOString?.() || null,
        officialQuitDateEnd: courierReportOfficialQuitDateRange[1]?.toISOString?.() || null
    });

    //#region handle funcs
    useLayoutEffect(() => {
        if (prevReportType !== "deliveries" && reportType === "deliveries") {
            const deliveriesSelectedColumsText = localStorage.getItem("deliveriesSelectedColumns");
            if (Boolean(deliveriesSelectedColumsText)) {
                setSelectedColumns(JSON.parse(deliveriesSelectedColumsText));
            }
        }
        if (prevReportType !== "courierReport" && reportType === "courierReport") {
            setSelectedTemplateId(undefined);
        }
    }, [reportType]);

    useEffect(() => {
        setSelectedCompany(undefined);
        setSelectedCompanies([]);
        setSelectedSubCompanies([]);
    }, [reportType]);

    useEffect(() => {
        const authCities = stores.meStore.auth.cities;
        if (reportType === SHIFTS_V2_REPORT && authCities?.length === 1) {
            setSelectedCities([authCities[0]]);
        }
    }, [reportType, stores.meStore.auth.cities]);

    const getPoolListCityFilter = () => {
        if (reportType === SHIFTS_V2_REPORT) {
            if (selectedCities?.length === 1 && selectedCities[0] !== "all") {
                return selectedCities[0];
            }
            return undefined;
        }
        if (!selectedCity || selectedCity === "all") {
            return undefined;
        }
        return selectedCity;
    };

    const getShiftsReportCitiesPayload = () =>
        (selectedCities || []).filter(city => city && city !== "all");

    const [isPoolLoading, pools] = useFetch({
        fetchFn: () =>
            stores.poolStore.getPoolList({
                limit: 1000,
                page: 1,
                city: getPoolListCityFilter()
            }),
        initialCall: true,
        dependencies: [reportType, selectedCity, selectedCities]
    });

    const onChangeReportType = async value => {
        setOrderList(null);
        setHoursList(null);
        setShiftsList(null);
        setPoolShiftList(null);
        setCourierFeedbackList(null);
        setCourierDedicationList(null);
        setSubcompanySalesList(null);
        setSubcompanyContractsList(null);
        setPrevReportType(reportType);
        setReportType(value);
        setSubCompaniesList(null);
        setUserReportList(null);
        setCourierReportList(null);
        setCourierReportDisabled(undefined);
        setCourierReportActivationStep(undefined);
        setCourierReportActivationSubStep(undefined);
        setCourierReportZoneManagementId(undefined);
        setCourierReportZoneId(undefined);
        setCourierReportLeaveStatus(undefined);
        setCourierReportApplicationDateRange([]);
        setCourierReportOfficialQuitDateRange([]);
        setSelectedCity(null);
        setSelectedCities([]);
        setPoolGroups([]);
        setPoolIdsShifts([]);
        if (["deliveryOrders", COMPANY_ORDERS_REPORT].includes(value)) {
            const endDate = moment()
                .endOf("day")
                .toDate();
            const startDate = moment()
                .subtract(1, "days")
                .startOf("day")
                .toDate();
            setDateRange([startDate, endDate]);
        } else if (value === "hours") {
            const endDate = moment()
                .endOf("day")
                .toDate();
            const startDate = moment()
                .subtract(3, "days")
                .startOf("day")
                .toDate();
            setDateRange([startDate, endDate]);
        } else {
            setDateRange([]);
        }
        if (value === "deliveries" || value === "deliveryOrders") {
            const result = await stores.orderStore.getRaportColumns({reportType: value});
            setReportColumns(result);
            setSelectedColumns(result.map(r => r.key));
        } else if (value === "courierReport") {
            const result = await stores.courierStore.getCourierReportColumns();
            setReportColumns(result);
            setSelectedColumns(result.map(r => r.key));
            const templates = await stores.reportTemplateStore.getTemplates("courierReport");
            const lastUsedTemplate = templates[0]?.lastUsedAt ? templates[0] : null;
            if (lastUsedTemplate) {
                setCourierReportDisabled(lastUsedTemplate.filters?.disabled);
                setCourierReportActivationStep(lastUsedTemplate.filters?.activationStep);
                setCourierReportActivationSubStep(lastUsedTemplate.filters?.activationSubStep);
                setCourierReportZoneManagementId(lastUsedTemplate.filters?.zoneManagementId);
                setCourierReportZoneId(lastUsedTemplate.filters?.zoneId);
                setCourierReportLeaveStatus(lastUsedTemplate.filters?.leaveStatus);
                setCourierReportApplicationDateRange(
                    lastUsedTemplate.filters?.applicationDateStart &&
                        lastUsedTemplate.filters?.applicationDateEnd
                        ? [
                              new Date(lastUsedTemplate.filters.applicationDateStart),
                              new Date(lastUsedTemplate.filters.applicationDateEnd)
                          ]
                        : []
                );
                setCourierReportOfficialQuitDateRange(
                    lastUsedTemplate.filters?.officialQuitDateStart &&
                        lastUsedTemplate.filters?.officialQuitDateEnd
                        ? [
                              new Date(lastUsedTemplate.filters.officialQuitDateStart),
                              new Date(lastUsedTemplate.filters.officialQuitDateEnd)
                          ]
                        : []
                );
                if (lastUsedTemplate.selectedColumns?.length > 0) {
                    setSelectedColumns(lastUsedTemplate.selectedColumns);
                }
                setSelectedTemplateId(lastUsedTemplate._id);
            } else {
                const courierReportSelectedColumnsText = localStorage.getItem(
                    "courierReportSelectedColumns"
                );
                if (Boolean(courierReportSelectedColumnsText)) {
                    setSelectedColumns(JSON.parse(courierReportSelectedColumnsText));
                }
            }
        } else {
            setReportColumns(null);
        }
    };
    //#endregion

    //#region requests
    const onClickOkButton = async () => {
        let href;
        let download;
        let start;
        let end;
        if (dateRange) {
            [start, end] = dateRange;
        }
        if (reportType === "hours") {
            const result = await stores.courierStore.getHoursReport({
                startDate: dateRange[0] ? new Date(start) : null,
                endDate: dateRange[1] ? new Date(end) : null,
                additionalColumns,
                dateOption: dateOption || null
            });
            await setHoursList(result);
            href = result;
            download = `${t("reportFilenames.hourList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "shifts") {
            const result = await stores.courierStore.getShiftsReport({
                startDate: dateRange[0] ? new Date(start) : null,
                endDate: dateRange[1] ? new Date(end) : null,
                version: "v1"
            });
            await setShiftsList(result);
            if (result?.length === 0) {
                return;
            }
            href = result;
            download = `${t("reportFilenames.shiftsList", {ns: "reportPage"})}-v1 - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === SHIFTS_V2_REPORT) {
            const result = await stores.courierStore.getShiftsReport({
                startDate: dateRange[0] ? new Date(start) : null,
                endDate: dateRange[1] ? new Date(end) : null,
                workType: workType,
                citiesFilter: getShiftsReportCitiesPayload(),
                poolIdsFilter: poolGroups,
                companyIdsFilter: selectedCompanies,
                subCompanyIdsFilter: selectedSubCompanies,
                version: "v2"
            });
            await setShiftsList(result);
            if (result?.length === 0) {
                return;
            }
            href = result;
            download = `${t("reportFilenames.shiftsList", {ns: "reportPage"})}-v2 - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "shiftEditLogs") {
            if (!dateRange[0] || !dateRange[1]) {
                message.error("Lütfen başlangıç ve bitiş tarihlerini seçiniz");
                return;
            }
            const result = await stores.courierStore.getShiftEditLogsReport({
                period: "daily",
                startDate: dateRange[0] ? moment(start).format("YYYY-MM-DD") : null,
                endDate: dateRange[1] ? moment(end).format("YYYY-MM-DD") : null
            });
            await setShiftEditLogsList(result);
            href = result;
            download = `Kurye Vardiya Güncelleme Kayıtları Raporu - ${moment(Date()).format(
                "DD-MM-YYYY-HH-mm-ss"
            )}.xlsx`;
        } else if (reportType === "poolShiftsReport") {
            const result = await stores.poolShiftStore.poolShiftsReport({
                startDate: dateRange[0] ? moment(start).format("YYYY-MM-DD") : "",
                endDate: dateRange[1] ? moment(end).format("YYYY-MM-DD") : "",
                poolIds: poolIdsShifts,
                excel: true
            });
            const base64Data = result?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            await setPoolShiftList(blobUrl);
            href = blobUrl;
            download = `${t("reportFilenames.poolShiftsList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "dailyShifts") {
            const result = await stores.courierStore.getDailyShiftsReport();
            await setShiftsList(result);
            href = result;
            download = `${t("reportFilenames.dailyShiftsList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "feedbacks") {
            const result = await stores.courierStore.getFeedbacksReport({
                startDate: dateRange[0] ? new Date(start) : null,
                endDate: dateRange[1] ? new Date(end) : null
            });
            await setCourierFeedbackList(result);
            href = result;
            download = `${t("reportFilenames.courierFeedbackList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "subCompanies") {
            const result = await stores.courierStore.getSubCompaniesReport();
            await setSubCompaniesList(result);
            href = result;
            download = `${t("reportFilenames.subCompaniesList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "userReport") {
            const result = await stores.userStore.getUserReport();
            await setUserReportList(result);
            href = result;
            download = `${"kullanici-raporu"}.xlsx`;
        } else if (reportType === "subcompanyContracts") {
            const result = await stores.companyStore.getSubcompanyContracts({
                active:
                    activeSubCompany === "all" || !activeSubCompany
                        ? undefined
                        : activeSubCompany === true
                        ? true
                        : false,
                workType:
                    workType === "all" || !workType
                        ? undefined
                        : workType === "pool"
                        ? "pool"
                        : "dedicated",
                contractDate: contractDate ? moment(contractDate).format("YYYY-MM-DD") : undefined
            });
            const base64Data = result?.data;
            const blobUrl = await b64DecodeToUrl(base64Data);
            await setSubcompanyContractsList(blobUrl);
            href = blobUrl;
            download = `${t("reportFilenames.subcompanyContractsList", {
                ns: "reportPage"
            })} - ${moment(Date()).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "dedications") {
            const result = await stores.courierStore.getDedicationsReport({
                startDate: dateRange[0] ? new Date(start) : null,
                endDate: dateRange[1] ? new Date(end) : null
            });
            await setCourierDedicationList(result);
            href = result;
            download = `${t("reportFilenames.courierDedicationList", {
                ns: "reportPage"
            })} - ${moment(Date()).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else if (reportType === "courierReport") {
            selectedColumns.length > 0 &&
                localStorage.setItem(
                    "courierReportSelectedColumns",
                    JSON.stringify(selectedColumns)
                );
            const columnList =
                selectedColumns.length > 0
                    ? [...selectedColumns]
                    : [...(reportColumns?.map(column => column.key) || [])];
            const result = await stores.courierStore.getCourierReport({
                disabled:
                    typeof courierReportDisabled === "boolean" ? courierReportDisabled : undefined,
                activationStep: courierReportActivationStep,
                activationSubStep: courierReportActivationSubStep,
                zoneManagementId: courierReportZoneManagementId,
                zoneId: courierReportZoneId,
                leaveStatus: courierReportLeaveStatus,
                applicationDateStart: courierReportApplicationDateRange[0]
                    ? new Date(courierReportApplicationDateRange[0])
                    : undefined,
                applicationDateEnd: courierReportApplicationDateRange[1]
                    ? new Date(courierReportApplicationDateRange[1])
                    : undefined,
                officialQuitDateStart: courierReportOfficialQuitDateRange[0]
                    ? new Date(courierReportOfficialQuitDateRange[0])
                    : undefined,
                officialQuitDateEnd: courierReportOfficialQuitDateRange[1]
                    ? new Date(courierReportOfficialQuitDateRange[1])
                    : undefined,
                selectedColumns: columnList
            });
            await setCourierReportList(result);
            href = result;
            download = `${t("reportFilenames.courierReport", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.csv`;
        } else if (reportType === "subcompanySales") {
            if (
                !selectedCompany &&
                (selectedCity === null || selectedCity === "all") &&
                (workType === null || workType === "all")
            ) {
                message.error("En az bir filtre seçiniz");
                return;
            }
            const result = await stores.companyStore.getSubCompanySalesReport({
                companyId: selectedCompany,
                workType: workType === "all" ? undefined : workType,
                city: selectedCity === "all" ? undefined : selectedCity
            });
            await setSubcompanySalesList(result);
            href = result;
            download = `Alt Firma Satış Raporu - ${moment(Date()).format(
                "DD-MM-YYYY-HH-mm-ss"
            )}.xlsx`;
        } else if (reportType === COMPANY_ORDERS_REPORT) {
            stores.appStore.setLoading(true);
            const response = await stores.orderStore.getOrdersReportForCompany({
                startDate: dateRange[0] ? new Date(start) : new Date(moment().subtract(1, "days")),
                endDate: dateRange[1] ? new Date(end) : new Date(),
                companies: selectedCompanies,
                subcompanies: selectedSubCompanies,
                poolIds: poolGroups,
                workType,
                city: selectedCity
            });
            stores.appStore.setLoading(false);
            if (!response) {
                return;
            }
            href = await b64DecodeToUrl(response.data);
            download = `${t("reportFilenames.orderList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        } else {
            selectedColumns.length > 0 &&
                localStorage.setItem("deliveriesSelectedColumns", JSON.stringify(selectedColumns));
            const columnList =
                selectedColumns.length > 0
                    ? [...selectedColumns]
                    : [...(reportColumns?.map(column => column.key) || [])];
            let result = undefined;
            if (reportType === "deliveryOrders") {
                result = await stores.orderStore.getDeliveryOrdersReport({
                    startDate: dateRange[0]
                        ? new Date(start)
                        : new Date(moment().subtract(1, "days")),
                    endDate: dateRange[1] ? new Date(end) : new Date(),
                    selectedColumns: columnList,
                    companies: selectedCompanies,
                    poolIds:
                        poolGroups.length === 0 ? pools?.data?.map(pool => pool._id) : poolGroups,
                    workType: workType,
                    city: selectedCity
                });
            }
            await setOrderList(result);
            href = result;
            download = `${t("reportFilenames.orderList", {ns: "reportPage"})} - ${moment(
                Date()
            ).format("DD-MM-YYYY-HH-mm-ss")}.xlsx`;
        }
        return {href, download};
    };
    //#endregion

    const handleReportDownload = () => {
        onClickOkButton().then(({href, download}) => {
            const link = document.createElement("a");
            link.href = href;
            link.download = download;
            link.click();
            setDownloadReport(true);
        });
    };

    const options = [
        {label: t("fields.courierType", {ns: "common"}), value: "courierType"},
        {label: t("fields.city", {ns: "common"}), value: "city"}
    ];
    const dateOptions = [
        {label: t("dateOptions.total", {ns: "reportPage"}), value: undefined},
        {label: t("dateOptions.day", {ns: "reportPage"}), value: "dateAsDays"},
        {label: t("dateOptions.dayHour", {ns: "reportPage"}), value: "dateAsHours"}
    ];
    //#region renders
    const renderCompanyFields = useMemo(() => {
        if (!["deliveryOrders", COMPANY_ORDERS_REPORT, SHIFTS_V2_REPORT].includes(reportType)) {
            return null;
        }
        const isShiftsV2Report = reportType === SHIFTS_V2_REPORT;
        const filterColProps = isShiftsV2Report
            ? {xs: 24, sm: 12, md: 6}
            : {xs: 12, sm: 8, md: 8, lg: 4, xl: 3};
        const isPoolFilterDisabled = isShiftsV2Report && workType === "dedicated";

        return (
            <Col span={24} style={{marginBottom: "-20px"}}>
                <Row gutter={[20, 0]}>
                    <Col {...filterColProps}>
                        <CityFilter
                            multiple={isShiftsV2Report}
                            forceShowAllOption={isShiftsV2Report}
                            showAllOption={!isShiftsV2Report}
                            allowClear={isShiftsV2Report}
                            value={isShiftsV2Report ? selectedCities : selectedCity}
                            onChange={value => {
                                if (isShiftsV2Report) {
                                    setSelectedCities(value || []);
                                    setPoolGroups([]);
                                } else {
                                    setSelectedCity(value);
                                }
                            }}
                        />
                    </Col>
                    {workType === "pool" || isShiftsV2Report ? (
                        <Col {...filterColProps}>
                            <h5>{t("fields.poolRegion", {ns: "pools"})}</h5>
                            <SelectField
                                mode="multiple"
                                data-testid="poolGroupSelect"
                                value={poolGroups}
                                placeholder={t("filters.addFilter", {ns: "common"})}
                                onChange={value => setPoolGroups(value)}
                                allowClear
                                showSearch
                                disabled={isPoolFilterDisabled}
                                style={{width: "100%"}}
                                filterOption={(input, option) =>
                                    normalizeText(
                                        option?.props?.["data-label"] ||
                                            option?.props?.children ||
                                            ""
                                    ).indexOf(normalizeText(input)) >= 0
                                }
                                optionFilterProp="children"
                            >
                                {pools?.data
                                    ? pools?.data.map(pool => (
                                          <SelectField.Option
                                              key={pool?._id}
                                              value={pool?._id}
                                              data-testid="poolGroups_option"
                                              data-label={pool?.name}
                                          >
                                              {pool?.name}
                                          </SelectField.Option>
                                      ))
                                    : ""}
                            </SelectField>
                        </Col>
                    ) : null}
                    <Col {...(isShiftsV2Report ? filterColProps : {span: 8})}>
                        <CompanyFilter
                            mode="multiple"
                            showPoolOption={false}
                            allKey="all"
                            value={selectedCompanies}
                            onChange={value => {
                                setSelectedCompanies(value);
                                setSelectedSubCompanies([]);
                            }}
                        />
                    </Col>
                    <Col {...(isShiftsV2Report ? filterColProps : {span: 8})}>
                        <h5>Alt Firma</h5>
                        <SubCompanyFilter
                            mode="multiple"
                            showAllOption={false}
                            value={selectedSubCompanies}
                            onChange={setSelectedSubCompanies}
                            companies={selectedCompanies}
                            disabled={!Boolean(selectedCompanies.length)}
                            hideTitle={true}
                        />
                    </Col>
                </Row>
            </Col>
        );
    }, [
        reportType,
        selectedCompanies,
        selectedSubCompanies,
        workType,
        poolGroups,
        selectedCity,
        selectedCities,
        pools
    ]);

    const getReportColumnsTranslationKey = reportType => {
        return reportType === "courierReport" ? "courierReport" : "deliveries";
    };

    const renderReportColumns = useMemo(() => {
        if (Boolean(reportColumns)) {
            const translationKey = getReportColumnsTranslationKey(reportType);
            const checkboxList = [];
            for (let column of reportColumns) {
                checkboxList.push({
                    label: t(`reportTypes.${translationKey}.${column.key}`, {ns: "reportPage"}),
                    value: column.key
                });
            }

            return (
                <Col span={24}>
                    <Row gutter={[20, 0]}>
                        <Col span="16">
                            <h5>{t("fieldsToAdd", {ns: "reportPage"})}</h5>
                            <SelectField
                                mode="multiple"
                                showSearch
                                allowClear
                                value={selectedColumns}
                                data-testid="columns"
                                onChange={setSelectedColumns}
                                filterOption={(input, option) =>
                                    option?.props["data-label"]
                                        ?.toLocaleLowerCase("TR-tr")
                                        ?.indexOf(input?.toLocaleLowerCase("TR-tr")) >= 0
                                }
                            >
                                {arrayAlphabeticalSort([...checkboxList], "label").map(
                                    (column, i) => (
                                        <SelectField.Option
                                            data-testid="columns_option"
                                            data-label={column.label}
                                            key={i}
                                            value={column.value}
                                        >
                                            {column.label}
                                        </SelectField.Option>
                                    )
                                )}
                            </SelectField>
                        </Col>
                    </Row>
                </Col>
            );
        } else {
            return null;
        }
    }, [reportColumns, selectedColumns, setSelectedColumns, reportType, i18n?.resolvedLanguage]);

    return useObserver(() => (
        <>
            <Row gutter={[0, 20]} align="bottom" data-testid="raporScreen">
                <Col span={24}>
                    <Col xs={24} sm={8} md={8} lg={6} xl={4}>
                        <h5>{t("reportSubject", {ns: "reportPage"})}</h5>
                        <SelectField
                            value={reportType}
                            data-testid="raporSelector"
                            onChange={onChangeReportType}
                        >
                            <SelectField.Option value="">
                                {t("choose", {ns: "reportPage"})}
                            </SelectField.Option>
                            {stores.meStore.checkRole("isAdmin") && (
                                <>
                                    <SelectField.Option
                                        value="userReport"
                                        data-testid="raporSelector_option"
                                        data-testvalue="userReport"
                                    >
                                        Kullanıcı Raporu
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="shiftEditLogs"
                                        data-testid="raporSelector_option"
                                        data-testvalue="shiftEditLogs"
                                    >
                                        <>
                                            Vardiya Güncelleme <br />
                                            Kayıtları Raporu
                                        </>
                                    </SelectField.Option>
                                </>
                            )}
                            {stores.meStore.checkRole("isAdmin") && (
                                <SelectField.Option
                                    value="poolShiftsReport"
                                    data-testid="raporSelector_option"
                                    data-testvalue="poolShiftsReport"
                                >
                                    Havuz Vardiya Raporu
                                </SelectField.Option>
                            )}
                            {stores.meStore.checkRole("isAdmin") && (
                                <SelectField.Option
                                    value="subcompanyContracts"
                                    data-testid="raporSelector_option"
                                    data-testvalue="subcompanyContracts"
                                >
                                    Alt Firma Kontratları
                                </SelectField.Option>
                            )}
                            {stores.meStore.checkRole("courierReport") ? (
                                <SelectField.Option
                                    value="courierReport"
                                    data-testid="raporSelector_option"
                                    data-testvalue="courierReport"
                                >
                                    {t("courierReport", {ns: "reportPage"})}
                                </SelectField.Option>
                            ) : null}
                            {stores.meStore.checkRole("isAgency") ? (
                                <>
                                    <SelectField.Option
                                        value="feedbacks"
                                        data-testid="raporSelector_option"
                                        data-testvalue="feedbacks"
                                    >
                                        {t("courierFeedbacks", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value={COMPANY_ORDERS_REPORT}
                                        data-testid="raporSelector_option"
                                        data-testvalue="companyOrders"
                                    >
                                        {t("orderReport", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="shifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="shifts"
                                    >
                                        {t("shiftsScore", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value={SHIFTS_V2_REPORT}
                                        data-testid="raporSelector_option"
                                        data-testvalue="shiftsV2"
                                    >
                                        {t("shiftsScoreV2", {ns: "reportPage"})}
                                    </SelectField.Option>
                                </>
                            ) : stores.meStore.checkRole("generalReportsList") ? (
                                <>
                                    <SelectField.Option
                                        value="hours"
                                        data-testid="raporSelector_option"
                                        data-testvalue="hours"
                                    >
                                        {t("hoursWorked", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="shifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="shifts"
                                    >
                                        {t("shiftsScore", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value={SHIFTS_V2_REPORT}
                                        data-testid="raporSelector_option"
                                        data-testvalue="shiftsV2"
                                    >
                                        {t("shiftsScoreV2", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="dailyShifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="dailyShifts"
                                    >
                                        {t("shiftsScoreDaily", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="feedbacks"
                                        data-testid="raporSelector_option"
                                        data-testvalue="feedbacks"
                                    >
                                        {t("courierFeedbacks", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="dedications"
                                        data-testid="raporSelector_option"
                                        data-testvalue="dedications"
                                    >
                                        {t("courierAssignments", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="deliveryOrders"
                                        data-testid="raporSelector_option"
                                        data-testvalue="deliveryOrders"
                                    >
                                        {t("orderReport", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="subCompanies"
                                        data-testid="raporSelector_option"
                                        data-testvalue="subCompanies"
                                    >
                                        {t("subCompanies", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="subcompanySales"
                                        data-testid="raporSelector_option"
                                        data-testvalue="subcompanySales"
                                    >
                                        {"Alt Firma Satış Raporu"}
                                    </SelectField.Option>
                                </>
                            ) : stores.meStore.checkRole("sChef") ? (
                                <>
                                    <SelectField.Option
                                        value="shifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="shifts"
                                    >
                                        {t("shiftsScore", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value={SHIFTS_V2_REPORT}
                                        data-testid="raporSelector_option"
                                        data-testvalue="shiftsV2"
                                    >
                                        {t("shiftsScoreV2", {ns: "reportPage"})}
                                    </SelectField.Option>
                                </>
                            ) : !stores.meStore.checkRole("isHrUser") &&
                              !stores.meStore.checkRole("isEquipmentManagerUser") &&
                              !stores.meStore.checkRole("isFinanceUser") ? (
                                <>
                                    <SelectField.Option
                                        value="deliveryOrders"
                                        data-testid="raporSelector_option"
                                        data-testvalue="deliveryOrders"
                                    >
                                        {t("orderReport", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="shifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="shifts"
                                    >
                                        {t("shiftsScore", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value={SHIFTS_V2_REPORT}
                                        data-testid="raporSelector_option"
                                        data-testvalue="shiftsV2"
                                    >
                                        {t("shiftsScoreV2", {ns: "reportPage"})}
                                    </SelectField.Option>
                                </>
                            ) : null}
                            {stores.meStore.checkRole("isSOperationUser") ? (
                                <>
                                    <SelectField.Option
                                        value="dailyShifts"
                                        data-testid="raporSelector_option"
                                        data-testvalue="dailyShifts"
                                    >
                                        {t("shiftsScoreDaily", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option
                                        value="subCompanies"
                                        data-testid="raporSelector_option"
                                        data-testvalue="subCompanies"
                                    >
                                        {t("subCompanies", {ns: "reportPage"})}
                                    </SelectField.Option>
                                </>
                            ) : (
                                <></>
                            )}
                        </SelectField>
                    </Col>
                </Col>
                {reportType === "poolShiftsReport" && (
                    <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                        <h5>{t("fields.poolRegion", {ns: "pools"})}</h5>
                        <SelectField
                            mode="multiple"
                            data-testid="poolGroupSelect"
                            value={poolIdsShifts}
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            onChange={value => setPoolIdsShifts(value)}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                normalizeText(option.props.children).indexOf(
                                    normalizeText(input)
                                ) >= 0
                            }
                            optionFilterProp="children"
                        >
                            {pools?.data
                                ? pools?.data.map(pool => (
                                      <>
                                          <SelectField.Option
                                              key={pool?._id}
                                              value={pool?._id}
                                              data-testid="poolGroups_option"
                                          >
                                              {pool?.name}
                                          </SelectField.Option>
                                      </>
                                  ))
                                : ""}
                        </SelectField>
                    </Col>
                )}
                {reportType === "subcompanyContracts" && (
                    <>
                        <Col xs={24} sm={8} md={8} lg={4} xl={3} style={{marginRight: "10px"}}>
                            <h5>Alt Firma Aktifliği</h5>
                            <SelectField value={activeSubCompany} onChange={setActiveSubCompany}>
                                <SelectField.Option key={"all"} value={"all"}>
                                    Tümü
                                </SelectField.Option>
                                <SelectField.Option key={"true"} value={true}>
                                    Aktif
                                </SelectField.Option>
                                <SelectField.Option key={"false"} value={false}>
                                    Pasif
                                </SelectField.Option>
                            </SelectField>
                        </Col>
                        <Col xs={24} sm={8} md={8} lg={4} xl={3} style={{marginRight: "10px"}}>
                            <h5>Tarih</h5>
                            <DateRangePicker
                                locale={locale}
                                format={"DD/MM/YYYY"}
                                onChange={date => {
                                    setContractDate(date);
                                }}
                            />
                        </Col>
                    </>
                )}
                {[
                    "deliveryOrders",
                    COMPANY_ORDERS_REPORT,
                    "subcompanyContracts",
                    SHIFTS_V2_REPORT
                ].includes(reportType) && (
                    <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                        <h5>
                            {["deliveryOrders", COMPANY_ORDERS_REPORT].includes(reportType)
                                ? "Sipariş türleri"
                                : "Alt Firma Çalışma Şekli"}
                        </h5>
                        <SelectField
                            data-testid="workType"
                            value={workType}
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            onChange={value => {
                                setWorkType(value);
                                if (value === "dedicated") {
                                    setPoolGroups([]);
                                }
                            }}
                            allowClear
                        >
                            <SelectField.Option key={"all"} value={"all"}>
                                Tümü
                            </SelectField.Option>
                            <SelectField.Option key={"pool"} value={"pool"}>
                                Havuz
                            </SelectField.Option>
                            <SelectField.Option key={"dedicated"} value={"dedicated"}>
                                Sabit
                            </SelectField.Option>
                        </SelectField>
                    </Col>
                )}
                {reportType &&
                    [
                        "dedications",
                        "dailyShifts",
                        "subCompanies",
                        "userReport",
                        "subcompanySales",
                        "subcompanyContracts",
                        "courierReport"
                    ].indexOf(reportType) === -1 && (
                        <>
                            <Col span={24}>
                                <Row gutter={[20, 20]}>
                                    <Col xs={12} sm={8} md={8} lg={4} xl={5}>
                                        <h5>{t("fields.startDate", {ns: "common"})}</h5>
                                        <DateRangePicker
                                            locale={locale}
                                            onChange={dates => {
                                                const updatedDate = [...dateRange];
                                                updatedDate[0] = dates;
                                                setDateRange(updatedDate);
                                            }}
                                            format={"DD/MM/YYYY"}
                                            placeholder={t("fields.startDate", {ns: "common"})}
                                            data-testid="startTime"
                                            value={dateRange[0] ? moment(dateRange[0]) : null}
                                        />
                                    </Col>
                                    <Col xs={12} sm={8} md={8} lg={4} xl={5}>
                                        <h5>{t("fields.endDate", {ns: "common"})}</h5>
                                        <DateRangePicker
                                            locale={locale}
                                            onChange={dates => {
                                                const updatedDate = [...dateRange];
                                                updatedDate[1] = dates;
                                                setDateRange(updatedDate);
                                            }}
                                            format={"DD/MM/YYYY"}
                                            placeholder={t("fields.endDate", {ns: "common"})}
                                            data-testid="endTime"
                                            value={dateRange[1] ? moment(dateRange[1]) : null}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                            {renderCompanyFields}
                            {renderReportColumns}
                        </>
                    )}
                {reportType === "hours" && (
                    <Col span={24}>
                        <Row gutter={[20, 20]}>
                            <Col xs={24} sm={8} md={8} lg={4} xl={5}>
                                <h5>{t("columnsToAdd", {ns: "reportPage"})}</h5>
                                <Checkbox.Group
                                    options={options}
                                    value={additionalColumns}
                                    data-test
                                    data-testid="additionalColumns"
                                    onChange={value => {
                                        setAdditionalColumns(value);
                                    }}
                                />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={4} xl={5}>
                                <h5>{t("filterBy", {ns: "reportPage"})}</h5>
                                <Radio.Group
                                    options={dateOptions}
                                    value={dateOption}
                                    data-testid="dateOptions"
                                    onChange={e => {
                                        setDateOption(e.target.value);
                                    }}
                                />
                            </Col>
                        </Row>
                    </Col>
                )}
                {reportType === "courierReport" && (
                    <Col span={24}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                <Card
                                    size="small"
                                    title={t("templateSelect", {ns: "reportPage"})}
                                    style={{
                                        backgroundColor: "#fafafa",
                                        borderColor: "#e8e8e8",
                                        marginBottom: 8
                                    }}
                                    bodyStyle={{padding: "12px 16px"}}
                                >
                                    <Row gutter={[12, 12]} align="middle">
                                        <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                                            <SelectField
                                                placeholder={t("templateSelect", {
                                                    ns: "reportPage"
                                                })}
                                                value={selectedTemplateId || undefined}
                                                onChange={value => {
                                                    if (!value) {
                                                        resetCourierReportToOriginal();
                                                        return;
                                                    }
                                                    const template = stores.reportTemplateStore
                                                        .getTemplatesForReportType("courierReport")
                                                        .find(t => t._id === value);
                                                    if (template) {
                                                        applyCourierReportTemplate(template);
                                                        stores.reportTemplateStore.markAsUsed(
                                                            value
                                                        );
                                                    }
                                                }}
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    normalizeText(option?.props?.children).indexOf(
                                                        normalizeText(input)
                                                    ) >= 0
                                                }
                                                optionFilterProp="children"
                                            >
                                                {stores.reportTemplateStore
                                                    .getTemplatesForReportType("courierReport")
                                                    .map(t => (
                                                        <SelectField.Option
                                                            key={t._id}
                                                            value={t._id}
                                                        >
                                                            {t.name}
                                                        </SelectField.Option>
                                                    ))}
                                            </SelectField>
                                        </Col>
                                        {stores.meStore.checkPermission(
                                            "reports.manageTemplates"
                                        ) && (
                                            <>
                                                <Col flex="none">
                                                    <Button
                                                        icon={<SaveOutlined />}
                                                        onClick={() => {
                                                            setTemplateName("");
                                                            setSaveTemplateModalVisible(true);
                                                        }}
                                                    >
                                                        {t("saveAsTemplate", {ns: "reportPage"})}
                                                    </Button>
                                                </Col>
                                                {selectedTemplateId && (
                                                    <>
                                                        <Col flex="none">
                                                            <Button
                                                                icon={<EditOutlined />}
                                                                onClick={() => {
                                                                    const template = stores.reportTemplateStore
                                                                        .getTemplatesForReportType(
                                                                            "courierReport"
                                                                        )
                                                                        .find(
                                                                            t =>
                                                                                t._id ===
                                                                                selectedTemplateId
                                                                        );
                                                                    setTemplateName(
                                                                        template?.name || ""
                                                                    );
                                                                    setEditTemplateModalVisible(
                                                                        true
                                                                    );
                                                                }}
                                                            >
                                                                {t("editTemplate", {
                                                                    ns: "reportPage"
                                                                })}
                                                            </Button>
                                                        </Col>
                                                        <Col flex="none">
                                                            <Button
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={() =>
                                                                    setDeleteTemplateModalVisible(
                                                                        true
                                                                    )
                                                                }
                                                            >
                                                                {t("deleteTemplate", {
                                                                    ns: "reportPage"
                                                                })}
                                                            </Button>
                                                        </Col>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Row>
                                </Card>
                            </Col>
                            <Col xs={24} sm={8} md={5} lg={5} xl={5}>
                                <h5>
                                    {t("courierReportFilters.activationStatus", {ns: "reportPage"})}
                                </h5>
                                <SelectField
                                    value={courierReportDisabled}
                                    onChange={setCourierReportDisabled}
                                    allowClear
                                >
                                    <SelectField.Option value={undefined}>
                                        {t("courierReportFilters.all", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option value={false}>
                                        {t("courierReportFilters.active", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option value={true}>
                                        {t("courierReportFilters.passive", {ns: "reportPage"})}
                                    </SelectField.Option>
                                </SelectField>
                            </Col>
                            <Col xs={24} sm={8} md={5} lg={5} xl={5}>
                                <h5>
                                    {t("courierReportFilters.courierStatus", {ns: "reportPage"})}
                                </h5>
                                <SelectField
                                    value={courierReportActivationStep}
                                    onChange={setCourierReportActivationStep}
                                    allowClear
                                >
                                    <SelectField.Option value="candidate">Aday</SelectField.Option>
                                    <SelectField.Option value="approved">
                                        Onaylanmış
                                    </SelectField.Option>
                                    <SelectField.Option value="blocked">
                                        Kara Liste
                                    </SelectField.Option>
                                    <SelectField.Option value="rejected">
                                        Olumsuz
                                    </SelectField.Option>
                                    <SelectField.Option value="terminated">
                                        İşten Çıkarıldı
                                    </SelectField.Option>
                                </SelectField>
                            </Col>
                            <Col xs={24} sm={8} md={5} lg={5} xl={5}>
                                <h5>
                                    {t("courierReportFilters.recruitmentStatus", {
                                        ns: "reportPage"
                                    })}
                                </h5>
                                <SelectField
                                    value={courierReportActivationSubStep}
                                    onChange={setCourierReportActivationSubStep}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                >
                                    {activationSubSteps
                                        .filter(
                                            s =>
                                                s.translation_key !== "all" &&
                                                s.translation_key !== "none"
                                        )
                                        .map(s => (
                                            <SelectField.Option
                                                key={s.value}
                                                value={s.value}
                                                label={t(s.translation_key, {
                                                    ns: "activationSubSteps"
                                                })}
                                            >
                                                {t(s.translation_key, {ns: "activationSubSteps"})}
                                            </SelectField.Option>
                                        ))}
                                </SelectField>
                            </Col>
                            <Col xs={24} sm={8} md={5} lg={5} xl={5}>
                                <h5>{t("courierReportFilters.leaveStatus", {ns: "reportPage"})}</h5>
                                <SelectField
                                    value={courierReportLeaveStatus}
                                    onChange={setCourierReportLeaveStatus}
                                    allowClear
                                >
                                    <SelectField.Option value={undefined}>
                                        {t("courierReportFilters.all", {ns: "reportPage"})}
                                    </SelectField.Option>
                                    <SelectField.Option value="onLeave">
                                        {t("courierReportFilters.onLeave", {ns: "reportPage"})}
                                    </SelectField.Option>
                                </SelectField>
                            </Col>
                            {stores.meStore.checkRole("zoneManagementSelect") ? (
                                <Col xs={24} sm={8} md={5} lg={5} xl={5}>
                                    <ZoneManagementSelect
                                        value={courierReportZoneManagementId}
                                        onChange={value => {
                                            setCourierReportZoneManagementId(value);
                                            setCourierReportZoneId(undefined);
                                        }}
                                        allowClear
                                        showTitle
                                        marginBottom="0px"
                                    />
                                </Col>
                            ) : null}
                            <Col xs={24} sm={8} md={4} lg={4} xl={4}>
                                <ZoneSelect
                                    value={courierReportZoneId}
                                    onChange={setCourierReportZoneId}
                                    zoneManagementId={courierReportZoneManagementId}
                                    allowClear
                                    showTitle
                                    marginBottom="0px"
                                />
                            </Col>
                            <Col xs={24} sm={12} md={10} lg={8} xl={7}>
                                <h5>
                                    {t("courierReportFilters.applicationDateRange", {
                                        ns: "reportPage"
                                    })}
                                </h5>
                                <DatePicker.RangePicker
                                    locale={locale}
                                    format={"DD/MM/YYYY"}
                                    style={{width: "100%"}}
                                    value={
                                        courierReportApplicationDateRange[0] &&
                                        courierReportApplicationDateRange[1]
                                            ? [
                                                  moment(courierReportApplicationDateRange[0]),
                                                  moment(courierReportApplicationDateRange[1])
                                              ]
                                            : null
                                    }
                                    onChange={dates => {
                                        if (dates) {
                                            setCourierReportApplicationDateRange([
                                                dates[0]?.toDate(),
                                                dates[1]?.toDate()
                                            ]);
                                        } else {
                                            setCourierReportApplicationDateRange([]);
                                        }
                                    }}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={10} lg={8} xl={7}>
                                <h5>
                                    {t("courierReportFilters.officialQuitDateRange", {
                                        ns: "reportPage"
                                    })}
                                </h5>
                                <DatePicker.RangePicker
                                    locale={locale}
                                    format={"DD/MM/YYYY"}
                                    style={{width: "100%"}}
                                    value={
                                        courierReportOfficialQuitDateRange[0] &&
                                        courierReportOfficialQuitDateRange[1]
                                            ? [
                                                  moment(courierReportOfficialQuitDateRange[0]),
                                                  moment(courierReportOfficialQuitDateRange[1])
                                              ]
                                            : null
                                    }
                                    onChange={dates => {
                                        if (dates) {
                                            setCourierReportOfficialQuitDateRange([
                                                dates[0]?.toDate(),
                                                dates[1]?.toDate()
                                            ]);
                                        } else {
                                            setCourierReportOfficialQuitDateRange([]);
                                        }
                                    }}
                                />
                            </Col>
                            {renderReportColumns}
                        </Row>
                    </Col>
                )}
                {reportType === "subcompanySales" && (
                    <Col span={24}>
                        <Row gutter={[20, 20]}>
                            <Col xs={12} sm={8} md={8} lg={4} xl={3}>
                                <CityFilter
                                    showAllOption
                                    value={selectedCity}
                                    onChange={value => {
                                        setSelectedCity(value);
                                    }}
                                />
                            </Col>
                            <Col xs={24} sm={8} md={8} lg={4} xl={3}>
                                <h5>Sipariş türleri</h5>
                                <SelectField
                                    data-testid="workType"
                                    value={workType}
                                    placeholder={t("filters.addFilter", {ns: "common"})}
                                    onChange={value => setWorkType(value)}
                                    allowClear
                                >
                                    <SelectField.Option key={"all"} value={"all"}>
                                        Tümü
                                    </SelectField.Option>
                                    <SelectField.Option key={"pool"} value={"pool"}>
                                        Havuz
                                    </SelectField.Option>
                                    <SelectField.Option key={"dedicated"} value={"dedicated"}>
                                        Sabit
                                    </SelectField.Option>
                                </SelectField>
                            </Col>
                            <Col span="8">
                                <CompanyFilter
                                    showPoolOption={false}
                                    allKey="all"
                                    value={selectedCompany}
                                    onChange={value => {
                                        setSelectedCompany(value);
                                        setSelectedSubCompanies([]);
                                    }}
                                />
                            </Col>
                        </Row>
                    </Col>
                )}
            </Row>
            <Row style={{marginTop: "20px"}}>
                {reportType && stores.meStore.checkPermission("reports.download") && (
                    <Tooltip title={t("actions.downloadReport", {ns: "reportPage"})}>
                        <DownloadButton
                            type="primary"
                            icon={<DownloadOutlined spin={stores.appStore.loading} />}
                            data-testid="downloadButton"
                            onClick={handleReportDownload}
                        />
                    </Tooltip>
                )}
            </Row>
            {reportType === "courierReport" && (
                <>
                    <Modal
                        title={t("saveAsTemplate", {ns: "reportPage"})}
                        visible={saveTemplateModalVisible}
                        onCancel={() => setSaveTemplateModalVisible(false)}
                        onOk={async () => {
                            if (!templateName?.trim()) {
                                message.error(t("templateNameRequired", {ns: "reportPage"}));
                                return;
                            }
                            const template = await stores.reportTemplateStore.createTemplate({
                                reportType: "courierReport",
                                name: templateName.trim(),
                                filters: getCourierReportFiltersFromState(),
                                selectedColumns
                            });
                            if (template) {
                                setSaveTemplateModalVisible(false);
                                setSelectedTemplateId(template._id);
                                message.success(t("templateSaved", {ns: "reportPage"}));
                            }
                        }}
                        okText={t("actions.save", {ns: "common"})}
                        cancelText={t("actions.cancel", {ns: "common"})}
                    >
                        <Input
                            placeholder={t("templateName", {ns: "reportPage"})}
                            value={templateName}
                            onChange={e => setTemplateName(e.target.value)}
                        />
                    </Modal>
                    <Modal
                        title={t("editTemplate", {ns: "reportPage"})}
                        visible={editTemplateModalVisible}
                        onCancel={() => setEditTemplateModalVisible(false)}
                        onOk={async () => {
                            if (!templateName?.trim()) {
                                message.error(t("templateNameRequired", {ns: "reportPage"}));
                                return;
                            }
                            const updated = await stores.reportTemplateStore.updateTemplate({
                                id: selectedTemplateId,
                                reportType: "courierReport",
                                name: templateName.trim(),
                                filters: getCourierReportFiltersFromState(),
                                selectedColumns
                            });
                            if (updated) {
                                setEditTemplateModalVisible(false);
                                message.success(t("templateSaved", {ns: "reportPage"}));
                            }
                        }}
                        okText={t("actions.save", {ns: "common"})}
                        cancelText={t("actions.cancel", {ns: "common"})}
                    >
                        <Input
                            placeholder={t("templateName", {ns: "reportPage"})}
                            value={templateName}
                            onChange={e => setTemplateName(e.target.value)}
                        />
                    </Modal>
                    <Modal
                        title={t("deleteTemplate", {ns: "reportPage"})}
                        visible={deleteTemplateModalVisible}
                        onCancel={() => setDeleteTemplateModalVisible(false)}
                        onOk={async () => {
                            const success = await stores.reportTemplateStore.deleteTemplate({
                                id: selectedTemplateId,
                                reportType: "courierReport"
                            });
                            if (success) {
                                setDeleteTemplateModalVisible(false);
                                resetCourierReportToOriginal();
                                message.success(t("templateDeleted", {ns: "reportPage"}));
                            }
                        }}
                        okText={t("actions.delete", {ns: "common"})}
                        cancelText={t("actions.cancel", {ns: "common"})}
                        okButtonProps={{danger: true}}
                    >
                        <p>{t("confirmDeleteTemplate", {ns: "reportPage"})}</p>
                    </Modal>
                </>
            )}
        </>
    ));
    //#endregion
};
